import { useCallback, useMemo } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  FormLayout,
  Box,
  Select,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { GenerativeTextField } from "@shared/ui";
import { Instructor } from "@features/events/types";
import { EventSlugField } from "@shared/ui";

interface OverviewCardProps {
  instructorData: Partial<Instructor>;
  onDataChange: (updates: Partial<Instructor>) => void;
  fieldErrors: Record<string, string | undefined>;
}

export default function OverviewCard({
  instructorData,
  onDataChange,
  fieldErrors,
}: OverviewCardProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{
    eventOrganizerId: string;
  }>();
  const { instructorTitleOptions, metadata } = useEventMetadata();
  const instructorTitles = useMemo(
    () => metadata?.instructorTitles ?? [],
    [metadata]
  );

  const handleTitleChange = useCallback(
    (value: string | null) => {
      onDataChange({ title: value as Instructor["title"] });
    },
    [onDataChange]
  );

  const selectedTitleInfo = useMemo(() => {
    if (!instructorData.title) return null;
    return instructorTitles.find(
      (title) => title.slug === instructorData.title
    );
  }, [instructorData.title, instructorTitles]);

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h2">
            {t("instructorDetails.overview.section_title")}
          </Text>
        </InlineStack>

        <FormLayout>
          <Box>
            <BlockStack gap="200">
              <Select
                label={t("instructorDetails.overview.title.title")}
                options={instructorTitleOptions}
                value={instructorData.title ?? null}
                onChange={handleTitleChange}
                error={fieldErrors.title}
                nullValueAllowed={true}
                nullValueLabel={t("instructorDetails.overview.title.null_value")}
                requiredIndicator={true}
              />
              {selectedTitleInfo && (
                <Text as="p" tone="subdued" variant="bodySm">
                  <Text as="span" fontWeight="bold">
                    {selectedTitleInfo.long}
                  </Text>{" "}
                  - {selectedTitleInfo.description}
                </Text>
              )}
            </BlockStack>
          </Box>

          <GenerativeTextField
            id="firstname"
            placementId="common.person_name"
            label={t("instructorDetails.overview.firstname")}
            value={instructorData.firstname ?? {}}
            onChange={(value) => onDataChange({ firstname: value })}
            type="textField"
            canGenerate={false}
            requiredIndicator
            error={fieldErrors.firstname}
            validateAllLanguages={true}
          />

          <GenerativeTextField
            id="lastname"
            placementId="common.person_name"
            label={t("instructorDetails.overview.lastname")}
            value={instructorData.lastname ?? {}}
            onChange={(value) => onDataChange({ lastname: value })}
            type="textField"
            canGenerate={false}
            requiredIndicator
            error={fieldErrors.lastname}
            validateAllLanguages={true}
          />

          <EventSlugField
            value={instructorData.slug || ""}
            onChange={(value) => onDataChange({ slug: value })}
            healthcareCompanyId={hcc || null}
            entityId={instructorData.id || undefined}
            entityType="instructor"
            error={fieldErrors.slug}
            requiredIndicator
          />
          <GenerativeTextField
            id="headline"
            placementId="event.instructor.headline"
            label={t("instructorDetails.overview.headline")}
            value={instructorData.headline ?? {}}
            onChange={(value) => onDataChange({ headline: value })}
            type="textField"
            canGenerate={true}
            error={fieldErrors.headline}
            requiredIndicator
            validateAllLanguages={true}
          />

          <GenerativeTextField
            id="bio"
            placementId="event.instructor.bio"
            label={t("instructorDetails.overview.bio.label")}
            description={t("instructorDetails.overview.bio.description")}
            value={instructorData.bio ?? {}}
            onChange={(value) => onDataChange({ bio: value })}
            type="textArea"
            canGenerate={true}
            error={fieldErrors.bio}
            requiredIndicator
            validateAllLanguages={true}
          />
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
